import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { DobrobutClinic, DrEleksVisit } from '@lib/core/models/dobrobut-entities.interface';
import { MemberProfile } from '@lib/core/models/member.interface';
import { PagedResponse } from '@lib/core/models/paged-response';
import { PharmacyOrder } from '@lib/core/models/pharmacy.interface';
import { Appointment, AppointmentSearchModel, BindAppointmentRequestModel, ExistingAppointmentSearchRequest, Visit } from '@lib/core/models/visit.interface';

import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class VisitsService {
  private readonly http = inject(HttpClient);

  selectedEleksVisit$ = new BehaviorSubject<DrEleksVisit | null>(null);

  getTodayAppointments(paging?: { page: number; pageSize: number }) {
    const options = {
      params: new HttpParams().set('Page', paging?.page).set('PageSize', paging?.pageSize),
    };

    return this.http.get<PagedResponse<Visit>>(`${environment.healthManagerApiUrl}/members/actions/today-appointments`, paging ? options : {});
  }

  bindEleksAppointment(actionId: string, data: BindAppointmentRequestModel) {
    return this.http.post(`${environment.healthManagerApiUrl}/members/actions/${actionId}/appointment/bind`, data);
  }

  updateBindEleksAppointment(appointmentId: string, data: BindAppointmentRequestModel) {
    return this.http.put(`${environment.healthManagerApiUrl}/members/appointments/${appointmentId}/bind`, data);
  }

  searchAllVisits(searchModel: AppointmentSearchModel) {
    return this.http.post<PagedResponse<Visit>>(`${environment.healthManagerApiUrl}/members/actions/search`, searchModel);
  }

  searchMemberAppointments(searchModel: AppointmentSearchModel) {
    return this.http.post<PagedResponse<Visit>>(`${environment.healthManagerApiUrl}/members/actions/appointments/search`, searchModel);
  }

  getMemberProfileFromAction(actionId: string) {
    return this.http.get<MemberProfile>(`${environment.healthManagerApiUrl}/members/actions/${actionId}`);
  }

  getDobrobutClinics() {
    return this.http.get<DobrobutClinic[]>(`${environment.healthManagerApiUrl}/members/dobrobut-clinics`);
  }

  getPharmacyOrderByActionId(actionId: string) {
    return this.http.get<PharmacyOrder>(`${environment.healthManagerApiUrl}/members/actions/${actionId}/pharmacy-order`);
  }

  getDrEleksVisits(userId: string) {
    return this.http.get<DrEleksVisit[]>(`${environment.healthManagerApiUrl}/members/by-user/${userId}/appointments/suggestions`);
  }

  searchExistingAppointments(requestBody: ExistingAppointmentSearchRequest): Observable<PagedResponse<Appointment>> {
    return this.http.post<PagedResponse<Appointment>>(`${environment.healthManagerApiUrl}/members/appointments/search`, requestBody);
  }

  createManualAppointment(value: unknown): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${environment.healthManagerApiUrl}/members/appointments`, value);
  }

  updateManualAppointment(appointmentId: string, value: unknown): Observable<{ id: string }> {
    return this.http.put<{ id: string }>(`${environment.healthManagerApiUrl}/members/appointments/${appointmentId}`, value);
  }

  cancelManualAppointment(appointmentId: string): Observable<{ cancellationStatusFailedToSync: boolean }> {
    return this.http.post<{ cancellationStatusFailedToSync: boolean }>(`${environment.healthManagerApiUrl}/members/appointments/${appointmentId}/cancel`, {});
  }

  completeManualAppointment(appointmentId: string): Observable<unknown> {
    const requestBody = {
      reason: 'Completed by health manager.',
      isMedicalDataExpected: true,
      didAppear: true,
    };
    return this.http.put<unknown>(`${environment.healthManagerApiUrl}/members/appointments/${appointmentId}/complete`, requestBody);
  }
}
